import React, { useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import styles from "./marker.module.scss";
import { useAppState } from "../../contexts/app/app.provider";

const roundPrice = (price) => {
  return price.toFixed(2).toString().replace(/\.00$/, "");
};
export const formatPrice = (price) => {
  if (price >= 1e9) {
    return `${roundPrice(Math.round((price / 1e9) * 10) / 10)} مليار`;
  }

  if (price >= 1e6) {
    return `${roundPrice(Math.round((price / 1e6) * 10) / 10)} مليون`;
  }

  if (price >= 1e3) {
    return `${roundPrice(Math.round((price / 1e3) * 10) / 10)} ألف`;
  }

  return roundPrice(price);
};

const Marker = ({
  text,
  title,
  shape,
  visited,
  active,
  onClick,
  zoom,
  zoomLimitToDisplayLabel,
  markerColor,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { isDailyRentPage, isRedMode } = useAppState();

  const mouseEnterHandler = () => {
    if (shape === "dot") {
      setShowTooltip(true);
    }
  };

  const mouseLeaveHandler = () => {
    if (shape === "dot") {
      setShowTooltip(false);
    }
  };

  const textClassName = classnames(styles.text, {
    [styles.circle]: shape === "circle",
    [styles.daily]: isDailyRentPage,
    [styles.red]: isRedMode,
  });

  const contentClassName = classnames(styles.content, {
    [styles.visited]: visited,
    [styles.active]: active,
    [styles.dot]: shape === "dot",
  });

  const markerClassName = classnames(styles.marker, {
    [styles.dot]: shape === "dot",
  });

  const arrowContentClassName = classnames(styles.arrowContent, {
    [styles.visited]: visited,
    [styles.daily]: isDailyRentPage,
    [styles.red]: isRedMode,
  });

  const arrowClassName = classnames(styles.arrow, {
    [styles.visited]: visited,
    [styles.red]: isRedMode,
  });

  const renderTooltip = () => (
    <div>
      <div className={styles.marker}>
        <div className={styles.content}>
          <div className={arrowClassName}>
            <span
              className={arrowContentClassName}
              style={{
                ...(markerColor && {
                  backgroundColor: markerColor,
                }),
              }}
            />
          </div>
          {!isRedMode && (
            <div
              style={{
                ...(markerColor && {
                  backgroundColor: markerColor,
                }),
              }}
              className={textClassName}
            >
              {formatPrice(text)}
            </div>
          )}
          {isRedMode && (
            <div
              className={textClassName}
              style={{
                ...(markerColor && {
                  backgroundColor: markerColor,
                }),
              }}
            >
              <div>{title}</div>
              <div className={styles.startingFrom}>{`يبدأ من ${formatPrice(
                text,
              )}`}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={markerClassName} onClick={onClick}>
      {showTooltip && renderTooltip()}
      <div
        className={contentClassName}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        {shape === "dot" && zoom >= zoomLimitToDisplayLabel && (
          <div className={styles.label}>{formatPrice(text)}</div>
        )}
        {shape !== "circle" && (
          <div className={arrowClassName}>
            <span
              className={arrowContentClassName}
              style={{
                ...(markerColor && {
                  backgroundColor: markerColor,
                }),
              }}
            />
          </div>
        )}
        {isRedMode && shape !== "circle" && (
          <div
            className={textClassName}
            style={{
              ...(markerColor && {
                backgroundColor: markerColor,
              }),
            }}
          >
            <div>{title}</div>
            <div className={styles.startingFrom}>{`يبدأ من ${formatPrice(
              text,
            )}`}</div>
          </div>
        )}
        {(!isRedMode || shape === "circle") && (
          <div
            style={{
              ...(markerColor && {
                backgroundColor: markerColor,
              }),
            }}
            className={textClassName}
          >
            {formatPrice(text)}
          </div>
        )}
      </div>
    </div>
  );
};

Marker.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  markerColor: PropTypes.string,
  onClick: PropTypes.func,
  shape: PropTypes.oneOf(["circle", "rectangle", "dot"]),
  visited: PropTypes.bool,
  active: PropTypes.bool,
  zoomLimitToDisplayLabel: PropTypes.number,
  zoom: PropTypes.number,
};

Marker.defaultProps = {
  text: "",
  title: "",
  markerColor: "",
  onClick: () => {},
  shape: "rectangle",
  visited: false,
  active: false,
  zoomLimitToDisplayLabel: 15,
  zoom: 0,
};

export default Marker;
